import type { FormBase } from "../types";
import type { FormBaseInstance } from "./Forms";
class EnvironmentUtils {

  public static getEnvVars(envvar: string): string | undefined {
    if (!envvar) {
      return undefined;
    }

    if ('__ENV__' in window
      && window["__ENV__"]
      && typeof window['__ENV__'] === "object"
      && envvar in window['__ENV__']) {
      return (window['__ENV__'] as { [x: string]: string; })[envvar];
    }

    return undefined;
  }

  private static availableComponents = {
    "icon": new DOMParser().parseFromString("<i></i>", "text/html").body.firstChild as HTMLElement,
    "button": new DOMParser().parseFromString("<button class='btn-placeholder' >boton</button>", "text/html").body.firstChild as HTMLButtonElement,
  };

  public static GetClientRect(element: HTMLElement): DOMRect {
    if (!element) {
      throw new Error("Element is not defined");
    }
    return element.getBoundingClientRect();
  }

  public static SetElementMeasurements(element: HTMLElement, cb: (el: HTMLElement) => void): void {
    if (!element) {
      throw new Error("Element is not defined");
    } else if (typeof cb !== "function") {
      throw new Error("Callback is not a function");
    }

    const elementOriginalWidth = parseFloat(element.style.width) || element.clientWidth;
    const elementOriginalHeight = parseFloat(element.style.height) || element.clientHeight;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const parent = element;
    let parentWidth = parent?.clientWidth ?? 0;
    let parentHeight = parent?.clientHeight ?? 0;
    let siblingsWidth = 0;
    let siblingsHeight = 0;

    const siblings = parent?.children;

    this.GetAndFixSiblingMeasurements(siblings, parent!, (sib) => {
      siblingsWidth += sib.clientWidth;
      siblingsHeight += sib.clientHeight;

      if (parentWidth >= windowWidth) {
        parentWidth = windowWidth;
      }

      if (parentHeight >= windowHeight) {
        parentHeight = windowHeight - 20;
      }

      const assignedWidth = Math.round(elementOriginalWidth * (parentWidth / siblingsWidth));
      const assignedHeight = Math.round(elementOriginalHeight * (parentHeight / siblingsHeight));

      if (elementOriginalWidth > assignedWidth || elementOriginalHeight > assignedHeight) {

        element.style.width = `${assignedWidth}px`;
        element.style.height = `${assignedHeight}px`;
      }
      cb(element);
    });
  }

  private static GetAndFixSiblingMeasurements(siblings?: HTMLCollection, parent?: Element, cb?: (el: HTMLElement) => void) {

    if (!siblings || !parent || !cb) {
      return;
    }

    const accumulator = { width: 0, height: 0 };
    for (let i = 0; i < siblings.length; i++) {
      const sibling = siblings.item(i);
      if (sibling instanceof HTMLElement) {
        accumulator.width += sibling.clientWidth;
        accumulator.height += sibling.clientHeight;
      }
    }

    if (accumulator.width >= parent.clientWidth || accumulator.height >= parent.clientHeight) {

      EnvironmentUtils.FixSibling(siblings, cb);

      accumulator.width = parent.clientWidth - 20; // 20px for padding
    }
  }

  private static FixSibling(siblings: HTMLCollection, cb: (el: HTMLElement) => void): void {
    let index = 0;
    let sibling: Element | null | undefined;

    while ((sibling = siblings.item(index)) !== null) {
      if (!sibling) {
        break;
      }
      EnvironmentUtils.SetElementMeasurements(sibling as HTMLElement, cb.bind(sibling as HTMLElement));
      index++;
    }
  }

  public static replaceTemplate(
    template: string,
  ) {

    const pattern = /{{(\w+)}}/g.exec(template)?.[1];
    if (!pattern) {
      return '';
    }
    const availableComponents = this.availableComponents;
    const replacementValue =
      availableComponents[pattern as keyof typeof availableComponents] as HTMLElement;
    return String.raw`<span>${template.replace(/{{\w+}}/g, replacementValue.outerHTML)}</span>
    `;
  }

  public static createFormInstance(form: FormBase): FormBaseInstance   {
    const formInstance: FormBase = { ...form };

    if (form.errors) {
      formInstance.errors = { ...form.errors };
    }

    return formInstance as FormBaseInstance;
  }

  public static translateToSpanish(word: string, firstCapital: boolean): string {
    const translations: { [key: string]: string; } = {
      "car": "auto",
      "health": "salud",
      "home": "hogar",
      "life": "vida",
      "enterprises": "empresas",
      "undefined": "indefinido",
      "name": "nombre",
      "surname": "apellido",
      "type": "tipo",
      "address": "dirección",
      "street": "calle",
      "city": "ciudad",
      "state": "estado",
      "zip": "código postal",
      "colony": "colonia",
      "dob": "fecha de nacimiento",
      "email": "email",
      "phone": "teléfono",
      "healthPreconditions": "precondiciones médicas",
      "sportsActivities": "actividades deportivas",
      "otherActivities": "otras actividades",
      "carBrand": "marca del auto",
      "carModel": "modelo del auto",
      "carYear": "año del auto",
      "coverageAmount": "monto de cobertura",
      "jobActivities": "actividades laborales",
      "buildingValue": "valor del edificio",
      "homeType": "tipo de vivienda",
      "numberOfFloors": "número de pisos",
      "securityMeasures": "medidas de seguridad",
      "companyName": "nombre de la empresa",
      "businessLine": "línea de negocio",
      "companyAddress": "dirección de la empresa",
      "companyPhone": "teléfono de la empresa",
      "companyEmail": "email de la empresa",
      "companyWebsite": "sitio web de la empresa",
      "companyDescription": "descripción de la empresa",
      "companyTaxId": "RFC de la empresa",
    };
    const translation = translations[word.toLowerCase()];

    if (translation && firstCapital) {
      return translation.charAt(0).toUpperCase() + translation.slice(1);
    }

    return translations[word] || word;
  }
}

export default EnvironmentUtils;
export { EnvironmentUtils };